import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WhyGermany from '../WhyGermany';
import ReactMarkdown from 'react-markdown';
import CommentsSection from './CommentsSection';
import { incrementLikes } from '../../services/blogService';

const BlogPost = ({ posts }) => {
  const { id } = useParams();
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [animateLike, setAnimateLike] = useState(false);

  const post = posts.find(p => String(p.id) === String(id));

  useEffect(() => {
    if (post) setLikes(post.likes || 0);
  }, [post]);

  if (!id) return <Navigate to="/blog" replace />;
  if (!post) {
    if (posts.length === 0) return <div className="min-h-screen flex items-center justify-center bg-white text-gray-500 font-medium">Loading insights...</div>;
    return <Navigate to="/blog" replace />;
  }

  // Special case for WhyGermany page
  if (post.isSpecialPage && post.content === 'whygermany') {
    return <WhyGermany />;
  }

  const handleLike = async () => {
    if (hasLiked) return;
    setLikes(prev => prev + 1);
    setHasLiked(true);
    setAnimateLike(true);
    await incrementLikes(post.id);
    setTimeout(() => setAnimateLike(false), 1000);
  };

  const displayImage = post.image_url || post.imageUrl || '/img/museum.png';
  const displayDateIso = post.date_published || post.date || new Date().toISOString();
  // Ensure contentSource is defined properly
  const contentSource = post.content_md || post.content || '';

  const relatedPosts = posts.filter(p => (
    String(p.id) !== String(post.id) &&
    ((p.category || '') === (post.category || '') || (p.tags || []).some(t => (post.tags || []).includes(t)))
  )).slice(0, 3);

  // Schema Generation
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: displayImage,
    datePublished: displayDateIso,
    author: { '@type': 'Person', name: post.author || 'Ravana Team' },
    publisher: { '@type': 'Organization', name: 'Ravana Migration', logo: { '@type': 'ImageObject', url: 'https://ravanamigration.com/img/logo.png' } },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ravanamigration.com' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://ravanamigration.com/blog' },
      { '@type': 'ListItem', position: 3, name: post.title, item: `https://ravanamigration.com/blog/${post.id}` }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{`${post.title} | Ravana Migration`}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:image" content={displayImage} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white font-sans selection:bg-blue-100">

        {/* Minimal Nav */}
        <div className="bg-white border-b border-gray-100 sticky top-0 z-40 backdrop-blur-md bg-white/90">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <nav className="flex items-center text-sm text-gray-500 gap-2">
              <Link to="/blog" className="hover:text-blue-600 font-medium transition-colors flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                Back
              </Link>
              <span className="text-gray-300">|</span>
              <span className="text-blue-600 font-bold uppercase text-xs tracking-wider">{post.category}</span>
            </nav>

            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${hasLiked ? 'bg-blue-50 text-blue-600 ring-1 ring-blue-100' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            >
              <svg className={`w-4 h-4 ${animateLike ? 'animate-bounce' : ''}`} fill={hasLiked ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>
              <span>{likes > 0 ? likes : 'Clap'}</span>
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 relative">

            {/* CONTENT (Left/Center - Expanded) */}
            <main className="lg:col-span-8 min-w-0">
              <header className="mb-10 text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
                  {post.title}
                </h1>
                <div className="flex items-center justify-center lg:justify-start gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                      {post.author?.[0] || 'R'}
                    </div>
                    <span className="font-semibold text-gray-900">{post.author || 'Ravana Team'}</span>
                  </div>
                  <span>•</span>
                  <time>{new Date(displayDateIso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</time>
                  <span>•</span>
                  <span>{post.readTime || '5 min read'}</span>
                </div>
              </header>

              {displayImage && (
                <figure className="mb-10 rounded-2xl overflow-hidden shadow-sm border border-gray-100 relative">
                  <img src={displayImage} alt={post.title} className="w-full h-auto object-cover" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-2xl"></div>
                </figure>
              )}

              <div className="prose prose-lg prose-gray max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-li:text-gray-600 prose-img:rounded-xl">
                <ReactMarkdown
                  components={{
                    h2: ({ node, ...props }) => {
                      const text = props.children[0];
                      const slug = String(text).toLowerCase().replace(/[^\w]+/g, '-');
                      return <h2 id={slug} className="scroll-mt-24 text-2xl font-bold mt-12 mb-4" {...props} />;
                    },
                    h3: ({ node, ...props }) => {
                      const text = props.children[0];
                      const slug = String(text).toLowerCase().replace(/[^\w]+/g, '-');
                      return <h3 id={slug} className="scroll-mt-24 text-xl font-bold mt-8 mb-3" {...props} />;
                    }
                  }}
                >
                  {contentSource}
                </ReactMarkdown>
              </div>

              {/* Tags */}
              <div className="mt-12 pt-6 border-t border-gray-100 flex flex-wrap gap-2">
                {post.tags?.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-gray-50 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Author Bio Box */}
              <div className="mt-12 p-8 bg-gray-50 rounded-2xl flex flex-col sm:flex-row items-center sm:items-start gap-6 border border-gray-100 text-center sm:text-left">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-2xl shrink-0">
                  {post.author?.[0] || 'R'}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">Written by {post.author || 'Ravana Team'}</h3>
                  <p className="text-gray-500 text-xs uppercase tracking-wider font-bold mb-3">Migration Expert</p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Helping students navigate the complex world of international education and migration. We provide expert guidance for Germany, Canada, UK, and more.
                  </p>
                </div>
              </div>

              {/* FAQs */}
              {post.faqs && post.faqs.length > 0 && (
                <div className="mt-12 bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
                  <div className="space-y-6">
                    {post.faqs.map((faq, i) => (
                      <div key={i}>
                        <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-16">
                <CommentsSection postId={post.id} />
              </div>
            </main>

            {/* RELATED (Right on desktop) */}
            <aside className="lg:col-span-4 lg:border-l lg:border-gray-100 lg:pl-12">
              <div className="sticky top-24">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">Related Insights</h4>
                {relatedPosts.length > 0 ? (
                  <div className="space-y-8">
                    {relatedPosts.map(p => (
                      <Link key={p.id} to={`/blog/${p.id}`} className="block group">
                        <div className="relative aspect-video rounded-lg overflow-hidden mb-3 bg-gray-100">
                          <img src={p.image_url || p.imageUrl} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <h5 className="font-bold text-gray-900 text-sm leading-snug group-hover:text-blue-600 transition-colors mb-2">
                          {p.title}
                        </h5>
                        <span className="text-xs text-gray-400 block">{new Date(p.date_published || p.date).toLocaleDateString()}</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No related posts found.</p>
                )}
              </div>
            </aside>

          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
